import React from 'react'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import Skeleton from '@mui/material/Skeleton'
import { formatCurrency } from '../../../helpers/UtilityHelper'
import { numberPrecision } from '../../../common/util'
import { kpiCardSx, accentColor } from '../../../common/sharedStyles'

const FORMAT = {
  number:   (v) => v == null ? '—' : numberPrecision(v),
  currency: (v) => v == null ? '—' : formatCurrency(v),
  percent:  (v) => v == null ? '—' : `${Number(v).toFixed(1)}%`,
  duration: (v) => {
    if (v == null) return '—'
    if (v < 60) return `${Math.round(v)}s`
    if (v < 3600) return `${Math.round(v / 60)}m`
    return `${(v / 3600).toFixed(1)}h`
  },
  minutes:  (v) => v == null ? '—' : `${Math.round(v)} min`,
}

export default function KPICard({ title, value, previousValue, icon, color = 'primary', format = 'number', loading }) {
  if (loading) {
    return (
      <Paper sx={{ p: 2.5 }}>
        <Skeleton width={100} height={16} />
        <Skeleton width={80} height={36} sx={{ mt: 1 }} />
        <Skeleton width={60} height={14} sx={{ mt: 0.5 }} />
      </Paper>
    )
  }

  const formatter = FORMAT[format] || FORMAT.number
  const displayValue = formatter(value)

  // Resolve color: if it's a known palette token use accentColor, otherwise treat as raw hex
  const resolvedColor = /^#/.test(color) ? color : accentColor(color)
  // Resolve kpiCardSx: use token if not a raw hex, otherwise fall back to primary
  const cardStyles = /^#/.test(color) ? kpiCardSx('primary') : kpiCardSx(color)

  let delta = null
  let deltaPositive = true
  if (previousValue != null && value != null && previousValue !== 0) {
    delta = ((value - previousValue) / previousValue * 100).toFixed(1)
    deltaPositive = delta >= 0
  }

  return (
    <Paper sx={{ ...cardStyles, position: 'relative', overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', top: 12, right: 12, opacity: 0.12, color: resolvedColor }}>
        {icon && React.cloneElement(icon, { sx: { fontSize: 48 } })}
      </Box>
      <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.68rem' }}>
        {title}
      </Typography>
      <Typography variant='h4' sx={{ mt: 0.5, fontWeight: 800, color: resolvedColor }}>
        {displayValue}
      </Typography>
      {delta !== null && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
          {deltaPositive
            ? <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
            : <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
          }
          <Typography variant='caption' sx={{ color: deltaPositive ? 'success.main' : 'error.main', fontWeight: 700 }}>
            {deltaPositive ? '+' : ''}{delta}%
          </Typography>
          <Typography variant='caption' sx={{ color: 'text.disabled' }}>vs prev period</Typography>
        </Box>
      )}
    </Paper>
  )
}
